/** The Roster API as React Query hooks. Saving is wholesale and versioned: a save states the version it read and the server refuses a stale one with 409 rather than overwriting. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import axios from 'axios'

import type { PayPeriod } from './payPeriod'
import type { ClearScope } from './roster/bulkClear'
import type { TrailingInvalidator } from './roster/invalidation'
import type { RosterAccessList } from './roster/rosterAccessList'
import type { RosterRemoval } from './roster/removal'
import { chunkWindow, type LeaveRow } from './roster/leave'
import type { PatchCell, PatchResponse, PlanDraft } from './roster/scopedDraft'
import type { ProjectRoster, RosterDb } from './roster/types'
import type { HiddenMode, HiddenProjects } from './roster/hiddenProjects'
import type { RowLayout } from './roster/rowLayout'
import type { TransitByProject } from './roster/projectsTab'
import { createTrailingInvalidator } from './roster/invalidation'
import { api, errorMessage } from './api'

/** True when a save or shift filing was refused for a real domain conflict; 409 is this API's general conflict status, so unrelated routes answer it too.
 *  Covers the PUT optimistic lock and the POST overwrite guard with one predicate. Not every 409 on the shifts route is the overwrite case (a closed payroll period also answers 409),
 *  so callers offering the overwrite check for `confirm_overwrite` first (`offersOverwrite`). */
export function isRosterConflict(error: unknown): boolean {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return false
  // A `published_view:` 409 is not "somebody saved first": the page must reload whole, so the conflict banner does not apply.
  if (isPublishedViewRefusal(error)) return false
  if (massClearCount(error) !== null) return false
  const method = error.config?.method?.toLowerCase()
  const url = error.config?.url ?? ''
  if (method === 'put' && url.endsWith('/api/roster')) return true
  if (method === 'post' && /\/api\/roster\/shifts\/[^/]+$/.test(url)) return true
  if (method === 'post' && /\/api\/roster\/plan\/(publish|clean)$/.test(url)) return true
  return false
}

/** The server's `detail` string, or '' for any other shape. Keyed on the code prefix, never on the prose after it. */
function refusalDetail(error: unknown): string {
  if (!axios.isAxiosError(error)) return ''
  const detail = error.response?.data?.detail
  return typeof detail === 'string' ? detail : ''
}

/** A row-layout save stated a version the server has moved past. */
export function isRosterLayoutStale(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 409 &&
    error.config?.method?.toLowerCase() === 'patch' &&
    (error.config?.url ?? '').endsWith('/api/roster/layout')
  )
}

/** The planned cells a save would blank, when the server refused it for that (`409 plan_mass_clear: N ...`); re-send with `confirmClear` to go ahead. */
export function massClearCount(error: unknown): number | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return null
  const match = /^plan_mass_clear: (\d+)/.exec(refusalDetail(error))
  return match ? Number(match[1]) : null
}

/** Which days and people that refusal would blank, or null when its tail cannot be read: the dialog then names the count alone. */
export function massClearScope(error: unknown): ClearScope | null {
  if (massClearCount(error) === null) return null
  const detail = refusalDetail(error)
  try {
    const scope = JSON.parse(detail.slice(detail.indexOf('{')))
    return typeof scope.from === 'string' && typeof scope.to === 'string' && Array.isArray(scope.people) ? scope : null
  } catch {
    return null
  }
}

/** The save was refused because the page holds the published, filtered view (`409 published_view:`); saved whole it would wipe every other project's data. Remedy: reload. */
export function isPublishedViewRefusal(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 409 &&
    refusalDetail(error).startsWith('published_view')
  )
}

/** A roster read or write was refused because the caller holds no roster role (`403 roster_access:`). Keyed on the prefix: an expired token is also a 403. */
export function isRosterAccessRefusal(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 403 &&
    refusalDetail(error).startsWith('roster_access')
  )
}

/** A manager-only roster route refused a caller who is not a roster manager (`403 roster manager required:`). Keyed on the prefix. */
export function isManagerRequiredRefusal(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 403 &&
    refusalDetail(error).startsWith('roster manager required')
  )
}


/** True for the overwrite refusal specifically: a closed payroll period also answers 409 but has no `confirm_overwrite` to offer.
 *  Checks the field name the backend sends, not the prose around it. */
export function offersOverwrite(error: unknown): boolean {
  return isRosterConflict(error) && errorMessage(error).includes('confirm_overwrite')
}

/** The POST body for a shift filing; pure so the `confirmOverwrite` default is provable. Only ShiftDialog's explicit Overwrite click passes `true`. */
export function shiftRequestBody(entry: ShiftEntry, confirmOverwrite?: boolean) {
  return { ...entry, confirm_overwrite: confirmOverwrite ?? false }
}

/** The document as served: the roster's own shape plus the version to save at. */
export type RosterPayload = RosterDb & { version: number | null }

/** The PUT body, built in one place so a test can pin it.
 *  ⛔ Never strip `view` or any served top-level key: the server's `view: "published"` 409 is the only guard against saving a stale filtered body whole. */
export function rosterSavePayload(version: number, db: RosterDb, confirmClear = false) {
  // ⛔ The row layout saves only through its own route, so a move that failed there cannot land here.
  const { planBase: _plan, planBaseProject: _planProject, rowLayout: _layout, ...body } = db
  return confirmClear ? { version, body, confirm_clear: true } : { version, body }
}

/** The caller's related projects' published plan; read-only and thin: no ids, actuals or section bodies. */
function getProjectRoster(): Promise<ProjectRoster> {
  return api.get<ProjectRoster>('/api/roster/me/project').then((r) => r.data)
}

/** The salary month containing `on` (default today), dates only. */
export function getPayPeriod(on?: string): Promise<PayPeriod> {
  return api
    .get<PayPeriod>('/api/roster/pay-period', { params: on ? { on } : {} })
    .then((r) => r.data)
}

export function useProjectRoster() {
  return useQuery({ queryKey: rosterKeys.project, queryFn: getProjectRoster })
}

export const rosterKeys = {
  document: ['roster', 'document'] as const,
  /** Named so `useFileShift` can ask `isMutating` whether another cell of the same batch is still in flight. */
  fileShift: ['roster', 'shift', 'file'] as const,
  /** Active HR staff not yet on the roster. */
  candidates: ['roster', 'candidates'] as const,
  /** What taking one person off the roster would do now. */
  removal: (personId: string) => ['roster', 'removal', personId] as const,
  /** The published plan of the caller's own project(s). */
  project: ['roster', 'project'] as const,
  /** The leave overlay for a drawn window. */
  leave: ['roster', 'leave'] as const,
  /** A staffer's draft cells. */
  draft: ['roster', 'plan-draft'] as const,
  /** The manager's staff-changes list. */
  /** Who holds which roster role (read-only). */
  access: ['roster', 'access'] as const,
}

/** T and DT days filed on the actual record, per roster project and person (roster managers only). Blank `start`/`end` count all time. */
export function useTransitByProject(start: string, end: string, enabled: boolean) {
  return useQuery({
    queryKey: ['roster', 'transit-by-project', start, end] as const,
    enabled,
    queryFn: async () =>
      (
        await api.get<TransitByProject>('/api/roster/transit-by-project', {
          params: { ...(start ? { start } : {}), ...(end ? { end } : {}) },
        })
      ).data,
  })
}

/** The schedule's row order and hidden set alone, stating the version it builds on; a stale one answers 409. */
export async function saveRowLayout(version: number, rowLayout: RowLayout | undefined) {
  return (
    await api.patch<{ version: number; rowLayout: RowLayout }>('/api/roster/layout', {
      version,
      rowLayout: rowLayout ?? { order: [], hidden: [] },
    })
  ).data
}

/** Hide or show one project on the schedule for every reader; the version is unchanged. */
export async function saveHiddenProject(mode: HiddenMode, project: string, hidden: boolean) {
  return (
    await api.put<{ hiddenProj: HiddenProjects }>('/api/roster/hidden-projects', { mode, project, hidden })
  ).data.hiddenProj
}

/** The hide as a mutation: the answer lands in the document cache, and a refusal re-reads what is stored. */
export function useSaveHiddenProject() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ mode, project, hidden }: { mode: HiddenMode; project: string; hidden: boolean }) =>
      saveHiddenProject(mode, project, hidden),
    onSuccess: (hiddenProj) => {
      queryClient.setQueryData<RosterPayload>(rosterKeys.document, (d) => (d ? { ...d, hiddenProj } : d))
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: rosterKeys.document, exact: true })
    },
  })
}

/** Hide or show one person on the schedule for every reader; the version is unchanged. */
export function useSaveHiddenPerson() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ mode, name, hidden }: { mode: HiddenMode; name: string; hidden: boolean }) =>
      (await api.put<{ hiddenPeople: HiddenProjects }>('/api/roster/hidden-people', { mode, name, hidden })).data.hiddenPeople,
    onSuccess: (hiddenPeople) => {
      queryClient.setQueryData<RosterPayload>(rosterKeys.document, (d) => (d ? { ...d, hiddenPeople } : d))
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: rosterKeys.document, exact: true })
    },
  })
}

/** How often an open, visible roster page looks for another manager's save. */
export const ROSTER_LIVE_MS = 30_000

/** `live` keeps the document current for a page that stays open; the query library pauses it while the tab is hidden. */
export function useRosterDocument(live = false, enabled = true) {
  return useQuery({
    queryKey: rosterKeys.document,
    queryFn: async () => (await api.get<RosterPayload>('/api/roster')).data,
    enabled,
    ...(live ? { refetchInterval: ROSTER_LIVE_MS, refetchOnWindowFocus: true } : {}),
  })
}

export function useSaveRoster() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ version, body, confirmClear }: { version: number; body: RosterDb; confirmClear?: boolean }) =>
      (await api.put<{ version: number }>('/api/roster', rosterSavePayload(version, body, confirmClear))).data,
    onSuccess: () => {
      // A save is where staff are appointed, so the access list moved.
      void queryClient.invalidateQueries({ queryKey: rosterKeys.access })
      return queryClient.invalidateQueries({ queryKey: rosterKeys.document })
    },
  })
}

/** What a Publish or a Clean moved on the server; `people` is who was told. */
export interface PlanWriteResult {
  version: number
  days: number
  people: number
  plannedUntil: string | null
}

function usePlanWrite<Vars>(url: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: Vars) => (await api.post<PlanWriteResult>(url, body)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: rosterKeys.document }),
  })
}

export function usePublishPlan() {
  return usePlanWrite<{ version: number; until: string }>('/api/roster/plan/publish')
}

export function useCleanPlan() {
  return usePlanWrite<{ version: number; from: string; to?: string }>('/api/roster/plan/clean')
}

/** One filed day. The five values are weights, not ticks: payroll sums the numbers. `project` is the roster's name; the server translates it to the guest log's spelling. */
export interface ShiftEntry {
  day: string
  project: string
  day_value: number
  night_value: number
  transit_value: number
  logging_value: number
  downtime_value: number
  /** Tasks done that day: a count, not a weight. */
  task_count: number
  lodge: boolean
  breakfast: boolean
  lunch: boolean
  dinner: boolean
  comment: string
}

/** File a shift. Invalidates the whole document, since the grid's `actual` is a projection of the guest log and guessing the code in the browser would duplicate the mapping.
 *  The invalidation is trailing but the writes are not: one call is one POST, awaited by the caller, and the document refetches once the burst goes quiet.
 *  ⛔ Do not parallelise the caller: the sequencing in `Schedule.fileSelection` is load-bearing, each POST re-derives that day's code stack server-side. */
export function useFileShift() {
  const queryClient = useQueryClient()
  // A ref, not state: nothing renders from it, and a render would rebuild the invalidator and drop its timer.
  const trailing = useRef<TrailingInvalidator | null>(null)
  if (trailing.current === null) {
    trailing.current = createTrailingInvalidator(
      () => queryClient.invalidateQueries({ queryKey: rosterKeys.document }),
      // Narrowed to this mutation: an unrelated save elsewhere would otherwise hold the refetch open.
      () => queryClient.isMutating({ mutationKey: rosterKeys.fileShift }) > 0,
    )
  }
  // ⛔ Flush on teardown, never cancel: a dropped timer leaves a cache that never heard about a successful write.
  useEffect(
    () => () => {
      // Discard the promise, not the call: the call runs the invalidation synchronously.
      void trailing.current?.flush()
    },
    [],
  )

  const mutation = useMutation({
    // Named so the invalidator can ask whether one of THESE is still running.
    mutationKey: rosterKeys.fileShift,
    mutationFn: async ({
      personId,
      entry,
      confirmOverwrite,
    }: {
      personId: string
      entry: ShiftEntry
      // Omitted is the ordinary path; only the confirm dialog passes `true`, on an explicit click.
      confirmOverwrite?: boolean
    }) =>
      (await api.post(`/api/roster/shifts/${personId}`, shiftRequestBody(entry, confirmOverwrite)))
        .data,
    onSuccess: () => trailing.current?.schedule(),
  })

  return {
    ...mutation,
    /** Force the pending invalidation now and wait for the refetch it starts; a write's own `onSuccess` only arms the trailing debounce.
     *  ⛔ Call it once, after the last write of a batch, never per cell: that would force one refetch per cell. */
    flushInvalidation: () => trailing.current?.flush() ?? Promise.resolve(),
  }
}

/** One active HR staff member not yet on the roster. `suggested_name` is the roster-style label the manager may edit before adding. */
export interface RosterCandidate {
  person_id: string
  name: string
  suggested_name: string
  /** English first name + initial; display only. Blank from an old server. */
  label?: string
  position: string
  team: string
  /** `staff_info.work_type`; the picker filters on it. */
  work_type: string
  /** The split names, Latin and Cyrillic; the picker searches both. */
  first_name: string
  last_name: string
  first_name_mn: string
  last_name_mn: string
  /** `staff_info.status`; not 'Active' means a leaver with days left. */
  status: string
  /** COALESCE(resignation_date, end_of_contract), ISO date or null. */
  last_day: string | null
}

/** Refetched after every add. */
export function useRosterCandidates() {
  return useQuery({
    queryKey: rosterKeys.candidates,
    queryFn: async () => (await api.get<RosterCandidate[]>('/api/roster/candidates')).data,
  })
}

/** Invalidates both the candidate list and the roster document: `people` is assembled fresh at read time, so the new row is invisible until it refetches. */
export function useAddStaffToRoster() {
  const queryClient = useQueryClient()
  return useMutation({
    // `home` is the picker's required Home project; the server also makes the person "can work on" it.
    mutationFn: async ({ personId, name, home }: { personId: string; name: string; home: string }) =>
      (
        await api.post<{ person_id: string; name: string }>('/api/roster/people', {
          person_id: personId,
          name,
          home,
        })
      ).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rosterKeys.candidates })
      void queryClient.invalidateQueries({ queryKey: rosterKeys.document })
    },
  })
}

/** Always refetched on open: a shift filed since would change the outcome. */
export function useRosterRemoval(personId: string) {
  return useQuery({
    queryKey: rosterKeys.removal(personId),
    queryFn: async () => (await api.get<RosterRemoval>(`/api/roster/people/${personId}/removal`)).data,
    staleTime: 0,
    gcTime: 0,
  })
}

/** Removed or marked left: either way the document's version moved, so it refetches. */
export function useRemoveFromRoster() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (personId: string) =>
      (await api.delete<RosterRemoval>(`/api/roster/people/${personId}`)).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rosterKeys.candidates })
      void queryClient.invalidateQueries({ queryKey: rosterKeys.access })
      void queryClient.invalidateQueries({ queryKey: rosterKeys.document })
    },
  })
}

/** Withdraw a filed day. Not the same as filing zeroes. */
export function useClearShift() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ personId, day }: { personId: string; day: string }) =>
      (await api.delete(`/api/roster/shifts/${personId}`, { params: { day } })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: rosterKeys.document }),
  })
}

/** The bulk clear's two halves: one day through the same DELETE `useClearShift` sends, and one refetch for the batch via `settle()`.
 *  Not `useClearShift` in a loop: its `onSuccess` invalidates per day and ShiftDialog's close depends on it. `clearOne` resolves to null or the refusal, not a throw. */
export function useBulkClearShifts() {
  const queryClient = useQueryClient()
  return {
    clearOne: async (
      personId: string,
      day: string,
    ): Promise<{ status: number | undefined; message: string } | null> => {
      try {
        await api.delete(`/api/roster/shifts/${personId}`, { params: { day } })
        return null
      } catch (err) {
        return {
          status: axios.isAxiosError(err) ? err.response?.status : undefined,
          message: errorMessage(err),
        }
      }
    },
    settle: () => queryClient.invalidateQueries({ queryKey: rosterKeys.document }),
  }
}


/** The server's `detail` prefix test shared by the predicates below. */
function hasRefusal(error: unknown, status: number, prefix: string): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === status &&
    refusalDetail(error).startsWith(prefix)
  )
}

/** A plan-cell PATCH lost the compare-and-set (`409 cell_conflict:`).
 *  ⛔ Not `isRosterConflict`: its remedy is to reload the draft, and it must never raise the manager's reload-and-replay banner. */
export function isCellConflict(error: unknown): boolean {
  return hasRefusal(error, 409, 'cell_conflict')
}

/** A PATCH named a cell outside the caller's scope (`403 roster_scope:`). */
export function isRosterScopeRefusal(error: unknown): boolean {
  return hasRefusal(error, 403, 'roster_scope')
}

/** The leave overlay for `from..to`; a window over 186 days is split into chunks (the server 422s beyond that). Pass `enabled: false` for anybody who may not read it. */
export function useRosterLeave(from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: [...rosterKeys.leave, from, to],
    enabled: enabled && Boolean(from) && Boolean(to),
    staleTime: 60_000,
    // Keep the previous window's rows while a scroll fetches the next, so the overlay does not blink.
    placeholderData: (previous) => previous,
    queryFn: async (): Promise<LeaveRow[]> => {
      const pages = await Promise.all(
        chunkWindow(from, to).map(async (w) =>
          (
            await api.get<{ rows: LeaveRow[] }>('/api/roster/leave', {
              params: { from: w.from, to: w.to },
            })
          ).data.rows,
        ),
      )
      return pages.flat()
    },
  })
}

/** The draft cells of the caller's home members. */
export function usePlanDraft(window: { from: string; to: string } | null, enabled: boolean) {
  return useQuery({
    queryKey: [...rosterKeys.draft, window?.from ?? '', window?.to ?? ''],
    enabled: enabled && window !== null,
    queryFn: async () =>
      (await api.get<PlanDraft>('/api/roster/plan/draft', { params: window })).data,
  })
}

/** One gesture's cells, compare-and-set, all or nothing (<= 500).
 *  ⛔ Every response carries a `version` the caller must adopt via `adoptPatchVersion`, even a no-op one. */
export function usePatchPlanCells() {
  return useMutation({
    mutationFn: async (cells: PatchCell[]) =>
      (await api.patch<PatchResponse>('/api/roster/plan/cells', { cells })).data,
  })
}

/** Managers, staff (with appointed and stale projects) and roster persons, for the People tab. Anyone but a roster manager gets a 403. */
export function useRosterAccessList(enabled: boolean) {
  return useQuery({
    queryKey: rosterKeys.access,
    enabled,
    queryFn: async () => (await api.get<RosterAccessList>('/api/roster/access')).data,
  })
}
